import React, { useEffect, useState, useContext, memo, useRef, useMemo, useCallback } from "react";
import { CommonTitleBar, ResizableComponent, StyleTooltip } from "../../common";
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { AiOutlineClear } from "react-icons/ai";
import IBSTable from "./IBSTable";
import { IBS_BUILDING_TABLE_COLUMNS } from "../../../../constants/Data";
import "../../common/ResizableComponent/styles.css"
import { fetchTbsOprtTableData } from "../../../../services/api/mapIBS.api";
import { WebMapContext } from "../../../../pages/sections/web-map/WebMapGoogleMap";
import { convertArrayToStringWithDelimeter } from "../../../../utils/helper";
import axios from "axios";
import { STATIC_ATTRS, UNIT_MAPPING } from "../../../../constants/WebMap";

const IBSTablePanel = ({
  operator,
  buildNames = [],
  buildTypes = [],
  connection_type,
  connection_types,
  scoreRangeFrom,
  scoreRangeTo,
  onCloseIconClick,
  savedBuild,
  handleSaveBuild,
  aggr,
  attrTypes = [],
  apiTrigger
}) => {
  let cancelToken;
  let timeOutForApiCall = 600;
  const tableDataTO = useRef();

  const {
    appliedFilters,
    geohashNE,
    geohashSW,
    getBbox,
    boundsChanged,
    globalConfig,
    mapPlacement,
    mapView
  } = useContext(WebMapContext);

  const { countryCode, year, quarter, level, levelVal } = appliedFilters;
  const [width, setWidth] = useState(100);
  const [height, setHeight] = useState(300);
  const [initTableData, setInitTableData] = useState([]);
  const [selectedBuild, setSelectedBuild] = useState(savedBuild);

  useEffect(() => {
    if (connection_type && aggr && attrTypes.length > 0) {
      fetchInitTableData();
    }
  }, [
    apiTrigger,
    levelVal,
    year,
    quarter,
    boundsChanged
  ])

  const fetchInitTableData = async () => {
    try {
      const data = await getTableData();
      if (data && Array.isArray(data)) {
        setInitTableData(data);
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
    }
  };

  const getSidePanelWidth = useCallback(() => {
    const element = document.getElementsByClassName("web-map-side-panel");
    if (element && element.length) {
      const viewportWidth = window.innerWidth; // Gets the current viewport width in pixels
      const index = mapPlacement === "left" ? 0 : 1;
      const newWidth = element[index].offsetWidth + (mapView === null ? 24 : 89);
      return ((newWidth / viewportWidth) * 100) - 4; // Converts px to vw
    }
  }, [mapView, mapPlacement]);

  const onResize = (event, { node, size }) => {
    setWidth(size.width);
    setHeight(size.height);
      };
 
  const getTableData = async (page = 1) => {
    if (typeof cancelToken !== typeof undefined) {
      cancelToken.cancel("Operation canceled due to new request.");
    }
    //Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();
    clearTimeout(tableDataTO.current);

    const { neBound, swBound } = getBbox;
    const northEast = convertArrayToStringWithDelimeter(
      geohashNE.length ? geohashNE : neBound,
      "_"
    );
    const southWest = convertArrayToStringWithDelimeter(
      geohashSW.length ? geohashSW : swBound,
      "_"
    );

    return new Promise((resolve, reject) => {
      tableDataTO.current = setTimeout(async () => {
        try {
          const { data } = await fetchTbsOprtTableData({
            country: countryCode,
            quarter: `${quarter} ${year}`,
            op: operator,
            ct: connection_type || connection_types[0]?.value,
            scoreFrom: scoreRangeFrom,
            scoreTo: scoreRangeTo,
            buildNames: buildNames.length
              ? convertArrayToStringWithDelimeter(buildNames, "_")
              : null,
            buildTypes: buildTypes.length
              ? convertArrayToStringWithDelimeter(buildTypes, "_")
              : null,
            ...(level && {
              level:
                level === "adminLevel1"
                  ? "adm1"
                  : level === "adminLevel2"
                    ? "adm2"
                    : "adm3",
            }),
            ...(levelVal && { levelVal }),
            northEast: northEast,
            southWest: southWest,
            ...(aggr && { aggr })
          }, page, 10, true , cancelToken);
          if (data) {
            resolve(data);
          } else {
            resolve([]);
          }
        } catch (error) {
          console.error('Error fetching table data:', error);
          reject(error);
        }
      }, timeOutForApiCall);
    });
  };

  const tableColumns = useMemo(() => {
    // refer Readme for sample full column structures
    let columnsArray = [...IBS_BUILDING_TABLE_COLUMNS];
    const attrTypesExceptSum = attrTypes.filter(x => STATIC_ATTRS.includes(x.value)); // rsrp, sinr etc
    const attrTypesForSum = attrTypes.slice(0, -1).filter(x => !STATIC_ATTRS.includes(x.value)); // Data trans, Data recei etc except ibs_score

    const attrColsExceptSum = attrTypesExceptSum.map(x => ({ label: x.label + ` (${UNIT_MAPPING[x.value]})`, field: `${aggr}_${x.value}`, type: "attrSubHeader" }));
    const attrColsForSum = attrTypesForSum.map(x => ({ label: x.label + ` (${UNIT_MAPPING[x.value]})`, field: `SUM_${x.value}`, type: "attrMainHeader", rowSpan: 2 }));

    const attrExceptSumIndexes = attrColsExceptSum.map((x, i) => i + attrColsForSum.length + 7); // place after Building Attribute (comes from IBS_BUILDING_TABLE_COLUMNS)
    const attrForSumIndexes = attrColsForSum.map((x, i) => aggr !== "SUM" ? i + 3 : i + 2); // place buffer +2(after provider) or +3(after Max)
    const admIdx = aggr !== "SUM" ? attrExceptSumIndexes[attrExceptSumIndexes.length - 1] : attrForSumIndexes[attrForSumIndexes.length - 1] + 4; // place ADMs after max_attr or attr+(ibs_score+d+r+bui_atr)(i.e. +4)

    // these are the items dynamically adding to table columns (ex: Max_rsrp, ADMs, etc)
    const dynamicColumns = [
      ...aggr !== "SUM" ? [{ label: aggr, field: aggr, colSpan: attrColsExceptSum?.length, type: "mainHeader" }] : [],
      ...attrColsForSum,
      ...aggr !== "SUM" ? [...attrColsExceptSum] : [],
      { label: globalConfig?.adminLevelLabels?.adm1, field: "a1", type: "subHeader", common: true },
      { label: globalConfig?.adminLevelLabels?.adm2, field: "a2", type: "subHeader", common: true },
      { label: globalConfig?.adminLevelLabels?.adm3, field: "a3", type: "subHeader", common: true }
    ];
    // defining dynamicColumns item positions in IBS_BUILDING_TABLE_COLUMNS (in other words, where to put items in dynamicColumns inside IBS_BUILDING_TABLE_COLUMNS)
    const indexPositions = [...aggr !== "SUM" ? [2] : [], ...attrForSumIndexes, ...aggr !== "SUM" ? [...attrExceptSumIndexes] : [], admIdx + 1, admIdx + 2, admIdx + 3];

    // finally make columns array by pushing dynamicColumns into fixed IBS_BUILDING_TABLE_COLUMNS
    dynamicColumns.forEach((column, index) => {
      const insertIndex = indexPositions[index];
      if (insertIndex !== undefined && insertIndex >= 0 && insertIndex <= columnsArray.length) {
        columnsArray.splice(insertIndex, 0, column);
      }
    });

    const totalDevicesIndex = columnsArray.findIndex(col => col.field === 'd');
    const totalSamplesIndex = columnsArray.findIndex(col => col.field === 'r');

    if (totalDevicesIndex !== -1 && totalSamplesIndex !== -1 && totalSamplesIndex !== totalDevicesIndex + 1) {
      const [totalSamplesCol] = columnsArray.splice(totalSamplesIndex, 1);
      columnsArray.splice(totalDevicesIndex + 1, 0, totalSamplesCol);
    }

    return columnsArray;
  }, [aggr, attrTypes])

  const providersCount = initTableData.map(data => data.ops.length);
  const totalProvidersCount = providersCount.reduce((acc, curr) => acc + curr, 0);
  const maxHieht = (totalProvidersCount * 52) + 110;
  const screenHeightBufferVH = 15;
  const screenHeightBufferPx = (screenHeightBufferVH / 100) * window.innerHeight;

  const dynamicMaxHeight = window.innerHeight - screenHeightBufferPx;

  return (
    <ResizableComponent
      resizeHandles={['n']}
      maxConstraints={[100, maxHieht < 200 ? 200 : maxHieht > dynamicMaxHeight ? dynamicMaxHeight : maxHieht]}
      minConstraints={[100, 60]}
      heightUnit="px"
      widthUnit="vw"
      width={width - getSidePanelWidth()}
      height={height}
      onResize={onResize}

    >
      <div style={{ border: '1px solid grey', padding: '1px' }}>
        <CommonTitleBar
          title={"Building Details"}
          onClick={() => { }}
        >
          {selectedBuild && (
            <>
              <div
                style={{
                  color: "white",
                  marginLeft: "5px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingRight: "10px",
                }}
                onClick={() => {
                  onCloseIconClick();
                  handleSaveBuild(selectedBuild);
                }}
              >
                <StyleTooltip title={"Visualize on map"}>
                  <img
                    src={"/assets/mappin.png"}
                    style={{ width: "15px", height: "15px" }}
                    alt="Icon"
                  />
                </StyleTooltip>
              </div>
              <StyleTooltip title={"Clear selection"}>
                <div
                  style={{
                    marginLeft: "5px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingRight: "12px",
                  }}
                >
                  <AiOutlineClear
                    style={{ cursor: "pointer", color: "white", fontSize: "20px" }}
                    onClick={() => {
                      setSelectedBuild();
                    }}
                  />
                </div>
              </StyleTooltip>
            </>
          )}
          <StyleTooltip title={"Close"}>
            <CancelOutlinedIcon
              style={{ cursor: "pointer", color: "white", fontSize: "20px" }}
              onClick={() => {
                onCloseIconClick();
                if (!selectedBuild) handleSaveBuild();
              }}
            />
          </StyleTooltip>
        </CommonTitleBar>
        <IBSTable
          columns={tableColumns}
          height={height}
          initData={initTableData}
          loadData={getTableData}
          selectedRow={selectedBuild}
          setSelectedRow={setSelectedBuild}
        />

      </div>
    </ResizableComponent>
  )

}

export default memo(IBSTablePanel);
